import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

import "./QuotesPage.css"; // <-- make sure this path matches where your css is

/* ---------------- helpers ---------------- */
const currency = (n, c = "GBP") => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "£ 0.00";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: c }).format(v);
  } catch {
    return `£ ${v.toFixed(2)}`;
  }
};

function formatDate(value) {
  if (!value) return "-";
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split("-");
    return `${d}-${m}-${y}`;
  }
  const dt = new Date(s);
  if (!isNaN(dt)) {
    const dd = String(dt.getDate()).padStart(2, "0");
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const yyyy = dt.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }
  return s;
}

function StatusBadge({ status }) {
  const label = (status || "draft").toLowerCase();
  const cls =
    label === "accepted"
      ? "qp-badge qp-badge--accepted"
      : label === "sent"
      ? "qp-badge qp-badge--sent"
      : label === "rejected"
      ? "qp-badge qp-badge--rejected"
      : label === "expired"
      ? "qp-badge qp-badge--expired"
      : "qp-badge qp-badge--draft";
  return <span className={cls}>{label}</span>;
}

// --- Email templates (backend-first, localStorage fallback) ---
const LS_KEYS = { quote: "jwcs.emailTemplate.quote" };
const DEFAULTS = {
  quote: `Hello,

Please find attached your quotation {{number}}.

Kind regards,`,
};

async function loadEmailTemplate(type) {
  try {
    const res = await fetch(`/api/email-templates?type=${encodeURIComponent(type)}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    return (json && json.message) || DEFAULTS[type];
  } catch {
    return localStorage.getItem(LS_KEYS[type]) || DEFAULTS[type];
  }
}

function mergePlaceholders(template, ctx) {
  return String(template || "")
    .replace(/\{\{number\}\}/g, ctx.number || "")
    .replace(/\{\{customer\}\}/g, ctx.customer || "")
    .replace(/\{\{company\}\}/g, ctx.company || "");
}

// GET /api/quotes?... -> { data, total }
async function fetchQuotes(params = {}) {
  const { q = "", status = "all", from, to, page = 1, pageSize = 20 } = params;
  const usp = new URLSearchParams();
  if (q) usp.set("q", q);
  if (status && status !== "all") usp.set("status", status);
  if (from) usp.set("from", from);
  if (to) usp.set("to", to);
  usp.set("page", String(page));
  usp.set("pageSize", String(pageSize));

  const res = await fetch(`/api/quotes?${usp.toString()}`);
  if (!res.ok) throw new Error((await res.text().catch(() => "")) || `HTTP ${res.status}`);
  const json = await res.json();

  const data = (json.data || []).map((row) => ({
    id: String(row.id),
    number: row.quote_number,
    customerName:
      `${row.first_name ?? ""} ${row.surname ?? ""}`.trim() || `Customer #${row.customer_id}`,
    createdAt: row.created_at,
    total: Number(row.total),
    currency: "GBP",
    status: (row.status || (row.approved ? "accepted" : "draft")).toLowerCase(),
    pdfUrl: `/api/quotes/${row.id}/pdf`,
  }));

  return { data, total: Number(json.total || data.length) };
}

/* ---------------- component ---------------- */
export default function QuotesPage() {
  const navigate = useNavigate();

  // filters / paging
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // data
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // selection
  const [selected, setSelected] = useState(() => new Set());
  const selectedIds = useMemo(() => Array.from(selected), [selected]);
  const selectedCount = selectedIds.length;
  const canSingleAct = selectedCount === 1;

  // pdf modal
  const [open, setOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(undefined);
  const [pdfName, setPdfName] = useState("quote.pdf");
  const [numPages, setNumPages] = useState(null);
  const [pdfErr, setPdfErr] = useState("");

  // responsive PDF width
  const pdfContainerRef = useRef(null);
  const [pdfWidth, setPdfWidth] = useState(900);

  // email state
  const [currentQuote, setCurrentQuote] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailMsg, setEmailMsg] = useState("");

  // delete state
  const [deletingId, setDeletingId] = useState(null);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / pageSize)),
    [total, pageSize]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await fetchQuotes({ q, status, from, to, page, pageSize });
      setRows(res.data);
      setTotal(res.total);
      // trim selection to visible
      setSelected((prev) => {
        const next = new Set(prev);
        const visible = new Set(res.data.map((r) => r.id));
        for (const id of Array.from(next)) if (!visible.has(id)) next.delete(id);
        return next;
      });
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [q, status, from, to, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  // resize PDF pages to fit modal width
  useEffect(() => {
    if (!open) return;

    function updateWidth() {
      if (pdfContainerRef.current) {
        const w = pdfContainerRef.current.clientWidth;
        setPdfWidth(w > 0 ? w : 900);
      }
    }

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [open]);

  // selection helpers
  const allOnPageSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someOnPageSelected = rows.some((r) => selected.has(r.id)) && !allOnPageSelected;

  function toggleSelect(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAllOnPage(checked) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const r of rows) {
        if (checked) next.add(r.id);
        else next.delete(r.id);
      }
      return next;
    });
  }

  // PDF helpers
  function onPdfLoadSuccess({ numPages }) {
    setNumPages(numPages);
  }

  async function openPdf(url, name = "quote.pdf") {
    if (!url) return;
    setOpen(true);
    setPdfErr("");
    setNumPages(null);
    setPdfName(name);

    if (pdfUrl && String(pdfUrl).startsWith("blob:")) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(undefined);
    }

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error((await res.text().catch(() => "")) || `HTTP ${res.status}`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      setPdfUrl(objUrl);
    } catch (e) {
      setPdfErr(String(e.message || e));
    }
  }

  function downloadCurrentPdf() {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = pdfName || "quote.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  async function sendEmailForQuote(row) {
    if (!row) return;
    setEmailSending(true);
    setEmailMsg("");
    try {
      const customer = row.customerName || "";
      const company =
        document?.querySelector('meta[name="app-company"]')?.getAttribute("content") || "";

      const template = await loadEmailTemplate("quote");
      const message = mergePlaceholders(template, {
        number: row.number,
        customer,
        company,
      });

      const res = await fetch(`/api/quotes/${row.id}/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: `Quotation ${row.number}`,
          message,
        }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }

      await load();
      const successMsg = "Email sent ✓";
      if (open) setEmailMsg(successMsg);
      else alert(successMsg);
    } catch (e) {
      const failMsg = `Failed to send: ${e.message || e}`;
      if (open) setEmailMsg(failMsg);
      else alert(failMsg);
    } finally {
      setEmailSending(false);
    }
  }

  async function emailCurrentQuote() {
    if (!currentQuote) return;
    await sendEmailForQuote(currentQuote);
  }

  async function deleteQuote(row) {
    if (!row) return;
    if (!window.confirm(`Delete quote ${row.number || row.id}? This cannot be undone.`)) return;

    setDeletingId(row.id);
    try {
      let res = await fetch(`/api/quotes/${row.id}`, { method: "DELETE" });

      if (!res.ok && (res.status === 404 || res.status === 405)) {
        res = await fetch(`/api/quotes/delete?id=${row.id}`, { method: "POST" });
      }

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Delete endpoint not implemented (HTTP ${res.status}).`);
      }

      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setTotal((t) => Math.max(0, (t || 0) - 1));
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    } catch (e) {
      alert(`Failed to delete: ${e.message || e}`);
    } finally {
      setDeletingId(null);
    }
  }

  function pickOne() {
    if (selectedCount !== 1) return null;
    const id = selectedIds[0];
    return rows.find((r) => r.id === id) || null;
  }

  function handleTopView() {
    const row = pickOne();
    if (!row) return;
    setCurrentQuote(row);
    openPdf(row.pdfUrl, `quote-${row.number || row.id}.pdf`);
  }

  function handleTopEdit() {
    const row = pickOne();
    if (!row) return;
    navigate(`/quote-builder?id=${row.id}`);
  }

  async function handleTopEmail() {
    const row = pickOne();
    if (!row) return;
    setCurrentQuote(row);
    await sendEmailForQuote(row);
  }

  function handleTopBook() {
    const row = pickOne();
    if (!row) return;
    const params = new URLSearchParams({
      quoteId: row.id,
      quoteNumber: row.number || "",
      name: row.customerName || "",
    });
    navigate(`/calendar?${params.toString()}`);
  }

  function handleTopDelete() {
    const row = pickOne();
    if (row) deleteQuote(row);
  }

  return (
    <div className="qp-container">
      <div className="qp-wrap">
        <div className="qp-card">
          <div className="qp-card__header">Quotes</div>

          <div className="qp-card__body">
            {/* Top actions */}
            <div className="q-topbar">
              <button className="qp-btn" onClick={handleTopView} disabled={!canSingleAct}>
                View
              </button>
              <button className="qp-btn" onClick={handleTopEdit} disabled={!canSingleAct}>
                Edit
              </button>
              <button
                className="qp-btn"
                onClick={handleTopEmail}
                disabled={!canSingleAct || emailSending}
              >
                {emailSending ? "Sending…" : "Email"}
              </button>
              <button className="qp-btn" onClick={handleTopBook} disabled={!canSingleAct}>
                Book Job in Calendar
              </button>
              <button
                className="qp-btn"
                onClick={handleTopDelete}
                disabled={!canSingleAct || deletingId === selectedIds[0]}
                style={{ borderColor: "#991b1b", background: "#991b1b", color: "#fee2e2" }}
                title={canSingleAct ? "Delete selected quote" : "Select exactly one quote"}
              >
                {deletingId === selectedIds[0] ? "Deleting…" : "Delete"}
              </button>

              <div className="muted" style={{ marginLeft: "auto" }}>
                {selectedCount > 0 ? `${selectedCount} selected` : "No quotes selected"}
              </div>
            </div>

            {/* Error banner */}
            {err && (
              <div
                style={{
                  marginBottom: 12,
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid #fecaca",
                  background: "#fee2e2",
                  color: "#991b1b",
                }}
              >
                {err}
              </div>
            )}

            {/* Filters */}
            <div className="qp-card q-filters">
              <div className="qp-card__header">Filters</div>
              <div className="qp-card__body q-filters__body">
                <div className="q-grid">
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label>Search</label>
                    <input
                      className="qp-input"
                      placeholder="Quote # or customer…"
                      value={q}
                      onChange={(e) => {
                        setPage(1);
                        setQ(e.target.value);
                      }}
                    />
                  </div>

                  <div>
                    <label>Status</label>
                    <select
                      className="qp-select"
                      value={status}
                      onChange={(e) => {
                        setPage(1);
                        setStatus(e.target.value);
                      }}
                    >
                      <option value="all">All</option>
                      <option value="draft">Draft</option>
                      <option value="sent">Sent</option>
                      <option value="accepted">Accepted</option>
                      <option value="rejected">Rejected</option>
                      <option value="expired">Expired</option>
                    </select>
                  </div>

                  <div>
                    <label>From</label>
                    <input
                      className="qp-input"
                      type="date"
                      value={from}
                      onChange={(e) => {
                        setPage(1);
                        setFrom(e.target.value);
                      }}
                    />
                  </div>

                  <div>
                    <label>To</label>
                    <input
                      className="qp-input"
                      type="date"
                      value={to}
                      onChange={(e) => {
                        setPage(1);
                        setTo(e.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="qp-table-scroll">
              <table className="qp-table q-table table">
                <thead className="qp-thead">
                  <tr>
                    <th className="qp-th q-col-select">
                      <input
                        type="checkbox"
                        aria-label="Select all on page"
                        checked={allOnPageSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someOnPageSelected;
                        }}
                        onChange={(e) => selectAllOnPage(e.target.checked)}
                      />
                    </th>
                    <th className="qp-th q-col-number">Quote #</th>
                    <th className="qp-th q-col-customer">Customer</th>
                    <th className="qp-th q-col-created">Created</th>
                    <th className="qp-th q-col-total">Total</th>
                    <th className="qp-th q-col-status">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr className="qp-row">
                      <td className="qp-td muted" colSpan={6} style={{ textAlign: "center" }}>
                        Loading…
                      </td>
                    </tr>
                  )}

                  {!loading && rows.length === 0 && (
                    <tr className="qp-row">
                      <td className="qp-td muted" colSpan={6} style={{ textAlign: "center" }}>
                        No quotes found.
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    rows.map((r) => (
                      <tr className="qp-row" key={r.id}>
                        <td className="qp-td q-col-select">
                          <input
                            type="checkbox"
                            aria-label={`Select quote ${r.number || r.id}`}
                            checked={selected.has(r.id)}
                            onChange={() => toggleSelect(r.id)}
                          />
                        </td>
                        <td className="qp-td q-col-number" style={{ fontWeight: 600 }}>
                          #{r.number}
                        </td>
                        <td className="qp-td q-col-customer">{r.customerName}</td>
                        <td className="qp-td q-col-created">{formatDate(r.createdAt)}</td>
                        <td className="qp-td q-col-total qp-right">
                          {currency(r.total, r.currency)}
                        </td>
                        <td className="qp-td q-col-status qp-right">
                          <StatusBadge status={r.status} />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="q-pagination">
              <div className="muted">
                {(total || 0).toLocaleString()} result{total === 1 ? "" : "s"}
              </div>

              <div className="q-pagination__controls">
                <button
                  className="qp-btn qp-btn--muted"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>

                <div className="muted q-pagination__page">
                  Page {page} / {totalPages}
                </div>

                <button
                  className="qp-btn qp-btn--muted"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PDF Modal */}
      {open && (
        <div className="qp-modal__backdrop" onClick={() => setOpen(false)}>
          <div className="qp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="qp-modal__header">Quote PDF Preview</div>

            <div className="qp-modal__body" ref={pdfContainerRef}>
              {pdfErr ? (
                <div
                  style={{
                    padding: 16,
                    borderRadius: 8,
                    border: "1px solid #fecaca",
                    background: "#fee2e2",
                    color: "#991b1b",
                  }}
                >
                  Failed to load PDF: {pdfErr}
                </div>
              ) : !pdfUrl ? (
                <div className="muted" style={{ padding: 24, textAlign: "center" }}>
                  Loading PDF…
                </div>
              ) : (
                <Document
                  file={pdfUrl}
                  onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                  onLoadError={(e) => setPdfErr(String(e?.message || e))}
                  onSourceError={(e) => setPdfErr(String(e?.message || e))}
                  loading={<div className="muted" style={{ padding: 24, textAlign: "center" }}>Loading…</div>}
                >
                  {Array.from(new Array(numPages || 0), (_, idx) => (
                    <Page key={`p_${idx + 1}`} pageNumber={idx + 1} width={pdfWidth} />
                  ))}
                </Document>
              )}
            </div>

            <div className="qp-modal__footer">
              {pdfUrl && (
                <>
                  <button className="qp-btn" onClick={downloadCurrentPdf}>
                    Download
                  </button>
                  <button className="qp-btn" onClick={async () => currentQuote && sendEmailForQuote(currentQuote)} disabled={emailSending}>
                    {emailSending ? "Sending…" : "Email to Customer"}
                  </button>
                  {emailMsg && (
                    <span
                      style={{
                        fontSize: 12,
                        marginRight: "auto",
                        color: emailMsg.startsWith("Email sent") ? "#15803d" : "#b91c1c",
                      }}
                    >
                      {emailMsg}
                    </span>
                  )}
                </>
              )}
              <button className="qp-btn qp-btn--muted" onClick={() => setOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}